import {
  compareStaffTasks,
  staffTaskDeadlineInstant,
  type StaffTask,
  type StaffTaskPriority,
  type StaffTaskStatus,
} from '@/lib/staffTasks';

export type StaffTaskSortColumn = 'title' | 'assignees' | 'deadline' | 'priority' | 'status';
export type StaffTaskSortDirection = 'asc' | 'desc';

export interface StaffTaskListSort {
  /** `null` keeps the clinic default: open first, overdue first, then deadline. */
  column: StaffTaskSortColumn | null;
  direction: StaffTaskSortDirection;
}

export const DEFAULT_STAFF_TASK_LIST_SORT: StaffTaskListSort = {
  column: null,
  direction: 'asc',
};

const TEXT: Intl.CollatorOptions = { sensitivity: 'base', numeric: true };

const PRIORITY_ORDER: Record<StaffTaskPriority, number> = {
  low: 0,
  normal: 1,
  high: 2,
};

const STATUS_ORDER: Record<StaffTaskStatus, number> = {
  open: 0,
  in_progress: 1,
  done: 2,
};

/**
 * Clicking the active column flips direction. Clicking another column
 * starts at A→Å / soonest / low→high.
 */
export function nextStaffTaskListSort(
  current: StaffTaskListSort,
  column: StaffTaskSortColumn,
): StaffTaskListSort {
  if (current.column === column) {
    return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { column, direction: 'asc' };
}

export function sortStaffTasks<T extends StaffTask>(
  rows: readonly T[],
  sort: StaffTaskListSort,
  now: Date = new Date(),
): T[] {
  return [...rows].sort((a, b) => compareStaffTaskListItems(a, b, sort, now));
}

export function compareStaffTaskListItems(
  a: StaffTask,
  b: StaffTask,
  sort: StaffTaskListSort,
  now: Date = new Date(),
): number {
  if (sort.column == null) return tie(a, b, now);

  let cmp = 0;
  switch (sort.column) {
    case 'title':
      cmp = compareText(a.title, b.title, sort.direction);
      break;
    case 'assignees':
      cmp = compareText(assigneeSortKey(a), assigneeSortKey(b), sort.direction);
      break;
    case 'deadline':
      cmp = compareTime(deadlineMillis(a), deadlineMillis(b), sort.direction);
      break;
    case 'priority':
      cmp = compareRank(PRIORITY_ORDER[a.priority], PRIORITY_ORDER[b.priority], sort.direction);
      break;
    case 'status':
      cmp = compareRank(STATUS_ORDER[a.status], STATUS_ORDER[b.status], sort.direction);
      break;
    default: {
      const unexpected: never = sort.column;
      return unexpected;
    }
  }
  if (cmp !== 0) return cmp;
  return tie(a, b, now);
}

function assigneeSortKey(task: StaffTask): string {
  if (task.assignees.length === 0) return '';
  return task.assignees.map((assignee) => assignee.name).join(', ');
}

function deadlineMillis(task: StaffTask): number | null {
  return staffTaskDeadlineInstant(task.deadline_date, task.deadline_time)?.getTime() ?? null;
}

/** Same open / overdue / deadline order the list uses before a column is chosen. */
function tie(a: StaffTask, b: StaffTask, now: Date): number {
  const cmp = compareStaffTasks(a, b, now);
  if (cmp !== 0) return cmp;
  return a.id.localeCompare(b.id);
}

/** Missing values stay last in both directions. */
function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: StaffTaskSortDirection,
): number {
  const aText = blankToNull(a);
  const bText = blankToNull(b);
  if (aText === null && bText === null) return 0;
  if (aText === null) return 1;
  if (bText === null) return -1;
  const cmp = aText.localeCompare(bText, 'da', TEXT);
  return direction === 'asc' ? cmp : -cmp;
}

function compareTime(
  a: number | null,
  b: number | null,
  direction: StaffTaskSortDirection,
): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  const cmp = a - b;
  return direction === 'asc' ? cmp : -cmp;
}

function compareRank(a: number, b: number, direction: StaffTaskSortDirection): number {
  const cmp = a - b;
  return direction === 'asc' ? cmp : -cmp;
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0 || trimmed === '—' || trimmed === '–' || trimmed === '-') return null;
  return trimmed;
}
